'use client'

import React from 'react'
import { cn } from '@repo/ui/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/tooltip'

interface DistributionBarProps {
  data: { [key: string]: number }
  total: number
  className?: string
  height?: number
  showLabels?: boolean
  colorTheme?: 'sex' | 'specimen' | 'default'
  animate?: boolean
  animationDelay?: number
}

interface DistributionEntry {
  key: string
  value: number
  percentage: number
  color: string
}

const COLOR_PALETTES = {
  sex: [
    'bg-accent-500',
    'bg-accent-600',
    'bg-accent-700',
    'bg-accent-400',
    'bg-accent-800',
    'bg-red-300',
    'bg-red-400',
    'bg-yellow-200',
    'bg-yellow-400',
  ],
  specimen: [
    'bg-mint-500',
    'bg-mint-600',
    'bg-mint-700',
    'bg-mint-400',
    'bg-mint-800',
    'bg-mint-300',
    'bg-mint-200',
    'bg-mint-900',
  ],
  default: [
    'bg-mint-500',
    'bg-accent-500',
    'bg-mint-600',
    'bg-accent-600',
    'bg-mint-700',
    'bg-accent-700',
    'bg-mint-400',
    'bg-accent-400',
    'bg-mint-800',
    'bg-accent-800',
  ],
} as const

export function DistributionBar({
  data,
  total,
  className,
  height = 8,
  showLabels = true,
  colorTheme = 'default',
  animate = false,
  animationDelay = 0,
}: DistributionBarProps) {
  const entries: DistributionEntry[] = React.useMemo(() => {
    const colors = COLOR_PALETTES[colorTheme]

    return Object.entries(data)
      .map(([key, value]) => ({
        key,
        value,
        percentage: total > 0 ? (value / total) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value)
      .map((entry, index) => ({
        ...entry,
        color: colors[index % colors.length],
      }))
  }, [data, total, colorTheme])


  return (
    <>
      <style jsx>{`
        @keyframes fillBar {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }
      `}</style>
      <div className={cn('w-full', className)}>
        <div className="flex items-center gap-2 mb-2" style={{ height: `${height}px` }}>
          {entries.map((entry, index) => (
            <TooltipProvider key={entry.key}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'h-full transition-all duration-200 hover:opacity-80 cursor-pointer',
                      entry.color,
                      animate && 'origin-left'
                    )}
                    style={{
                      width: `${entry.percentage}%`,
                      ...(animate && {
                        transform: 'scaleX(0)',
                        animation: `fillBar 0.4s ease-out ${animationDelay + (index * 400)}ms forwards`
                      })
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent className="bg-grey-800 text-white border-grey-700">
                  <div className="text-center">
                    <div className="font-semibold text-white">{entry.key}</div>
                    <div className="text-sm text-grey-200">
                      {entry.value.toLocaleString()} ({entry.percentage.toFixed(1)}%)
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>

        {showLabels && (
          <div className="flex flex-wrap gap-2 mt-2">
            {entries.map((entry, index) => (
              <div key={entry.key} className="flex items-center gap-1">
                <div className={cn('w-3 h-3 rounded-sm', entry.color)} />
                <span className="text-xs text-grey-600">
                  {entry.key} ({entry.percentage.toFixed(1)}%)
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
